import { useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEBOUNCE_MS = 200;

// City field with suggestions drawn from cities the community has already used.
//
// Deliberately NOT a closed list: there's no city master data behind this, just
// what people have typed before, so anything can still be entered freely and
// becomes a suggestion for the next person. The dropdown is a shortcut that
// nudges spellings to converge, not a validator.
const CityInput = ({
  value,
  onChange,
  placeholder = "Mumbai",
  id,
  testId = "city-input",
  className = "",
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);
  const reqRef = useRef(0);

  // Fetch on every change of the typed value, including empty (which returns
  // the most-used cities — useful before the first keystroke).
  useEffect(() => {
    if (!open) return;
    const mine = ++reqRef.current;
    const t = setTimeout(() => {
      axios
        .get(`${API}/cities/suggest`, { params: { q: value || "" } })
        .then((r) => {
          if (reqRef.current !== mine) return; // a newer keystroke won
          setSuggestions(r.data?.items || []);
        })
        .catch(() => setSuggestions([]));
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value, open]);

  // Close when focus leaves the field and its list.
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (city) => {
    onChange(city);
    setOpen(false);
    setHighlight(-1);
  };

  // An exact match is already typed — showing it as the only suggestion is noise.
  const visible = suggestions.filter(
    (s) => s.toLowerCase() !== (value || "").trim().toLowerCase()
  );

  const onKeyDown = (e) => {
    if (!open || visible.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % visible.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? visible.length - 1 : h - 1));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(visible[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        id={id}
        type="text"
        value={value || ""}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        maxLength={60}
        className={
          className ||
          "w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
        }
        data-testid={testId}
      />
      {open && visible.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white border border-[#E4E1D9] rounded-md shadow-lg"
          data-testid={`${testId}-suggestions`}
          role="listbox"
        >
          {visible.map((city, i) => (
            <li key={city}>
              <button
                type="button"
                // mousedown fires before the input's blur, so the click isn't
                // lost to the list closing underneath the pointer.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(city);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  i === highlight ? "bg-[#F0EFEB] text-[#2D302A]" : "text-[#4A4A48] hover:bg-[#F9F8F6]"
                }`}
                role="option"
                aria-selected={i === highlight}
              >
                {city}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CityInput;
